import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { anykrowdApi } from '@/api/anykrowdApi';
import { DynamicPage } from '@/shared/interfaces/dynamicPage';
import useStorageService from '@/shared/composables/useStorageService';

// Get pages
const getPages = async (): Promise<DynamicPage[]> => {
	const { data } = await anykrowdApi.get<DynamicPage[]>('/pages', false);
	return data;
};

// usePages
const usePages = () => {
	// Init
	const pages = ref<DynamicPage[]>();
	const { get, set } = useStorageService();

	const {
		data: pagesData,
		isFetching,
		isError,
		error,
		refetch: refetchPages,
		remove: removePagesFromCache,
	} = useQuery({
		queryKey: ['pages'],
		queryFn: () => getPages(),
		retry: 3,
		cacheTime: 1000 * 60 * 60 * 1, // 1 hour
	});

	watch(
		pagesData,
		() => {
			if (pagesData.value) {
				// Filter only enabled pages for navigation
				pages.value = pagesData.value.filter((page) => page.enabled === 1);

				// Update cache
				set('pages', pagesData.value);
			}
		},
		{ immediate: true },
	);

	return {
		pages,
		isFetchingPages: computed(() => isFetching.value),
		isErrorPages: computed(() => isError.value),
		pagesError: computed(() => error.value),
		refetchPages,
		removePagesFromCache,
		getPages,
	};
};

export default usePages;
